import React, { useContext, useMemo } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { Button, Dropdown, Modal } from 'ncoded-component-library';
import api from 'api';
import useMatchMedia from 'hooks/useMatchMedia';
import {
  DATA_ROOM_NODE_ACCESS_STATES,
  DataRoomNodeAccessState,
  DataRoomNodeWithStateAndSignal,
} from 'models/Lead';
import { RELEASE_FILE_ACCESS_COPY } from '../../../../../ReleaseFiles/components/ReleaseFileInstance/ReleaseFileInstance.component';
import DisplayFilesByLeadFile from '../DisplayFilesByLeadFile';
import { ACCESS_STATE_ICON, POPUP_HINT_COPY } from '../../data';
import { getHasElementsInArray } from 'utils';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import useCallbackAsync from 'hooks/useCallbackAsync';
import LinkToIcon from 'icons/LinkTo.icon';
import { getDataRoomNodeAccessDisplayState } from '../../../../utils';
import DisplayFilesByCategoryTrigger from '../DisplayFilesByCategoryTrigger';

import './DisplayFilesByLeadCategory.styles.scss';

const POPUP_CONTENT = {
  [DATA_ROOM_NODE_ACCESS_STATES.VIEWED]: 'viewedFiles',
  [DATA_ROOM_NODE_ACCESS_STATES.NOT_VIEWED]: 'sharedFiles',
  [DATA_ROOM_NODE_ACCESS_STATES.PENDING]: 'pendingViewFiles',
  [DATA_ROOM_NODE_ACCESS_STATES.NO_ACCESS]: 'notSharedFiles',
};

type DisplayFilesByLeadCategoryProps = {
  state: DataRoomNodeAccessState;
  count: number;
  isOpen: boolean;
  nodes: DataRoomNodeWithStateAndSignal[];
  leadId: string;
  disabled: boolean;
  onOpen: () => void;
  onClose: () => void;
  onToggleOpen: () => void;
  onCycleAccess: (
    node: DataRoomNodeWithStateAndSignal,
    currentState: DataRoomNodeAccessState,
  ) => void;
};

const DisplayFilesByLeadCategory: React.FC<DisplayFilesByLeadCategoryProps> = (
  props,
) => {
  const {
    state,
    count,
    isOpen,
    nodes,
    leadId,
    disabled,
    onOpen,
    onClose,
    onToggleOpen,
    onCycleAccess,
  } = props;

  const { t } = useTranslation();
  const isAboveLaptop = useMatchMedia('isAboveLaptop');

  const { popSuccess } = useContext(PopNotificationsContext);

  const Icon = ACCESS_STATE_ICON[state];

  const canOpen = useMemo(() => count > 0 || isOpen, [count, isOpen]);

  const pendingNodes = useMemo(
    () =>
      nodes.filter(
        (node) =>
          getDataRoomNodeAccessDisplayState(node) ===
          DATA_ROOM_NODE_ACCESS_STATES.PENDING,
      ),
    [nodes],
  );

  const canSendReminder = useMemo(
    () =>
      !disabled &&
      state === DATA_ROOM_NODE_ACCESS_STATES.PENDING &&
      getHasElementsInArray(nodes),
    [disabled, nodes, state],
  );

  const [handleSendReminder, isSendingReminder] = useCallbackAsync(async () => {
    await api.portfolio.seller.sendPendingFilesReminder(
      leadId,
      pendingNodes.map(({ dataRoomNodeId }) => dataRoomNodeId),
    );

    popSuccess(t('reminderSentSuccesfully'));
  });

  const classes = classNames('display-files-by-lead__category', {
    clickable: canOpen,
  });

  const content = (
    <div
      onClick={(e) => e.stopPropagation()}
      className="display-files-by-lead__popup__content"
    >
      <div
        className={classNames('display-files-by-lead__popup__header', state)}
      >
        <Icon />
        <span>{t(RELEASE_FILE_ACCESS_COPY[state])}</span>
      </div>
      <p className="display-files-by-lead__popup__description">
        {t(POPUP_HINT_COPY[state])}
      </p>

      {getHasElementsInArray(nodes) ? (
        <div className="display-files-by-lead__popup__list">
          {nodes.map((node) => (
            <DisplayFilesByLeadFile
              key={node.dataRoomNodeId}
              node={node}
              leadId={leadId}
              disabled={disabled}
              onCycleAccess={onCycleAccess}
            />
          ))}
        </div>
      ) : (
        <p className="display-files-by-lead__popup__empty">
          {t('noFilesAvailable')}
        </p>
      )}

      {canSendReminder && (
        <>
          <Button
            variant="outline"
            icon={LinkToIcon}
            disabled={isSendingReminder || !getHasElementsInArray(pendingNodes)}
            onClick={handleSendReminder}
          >
            {t('sendReminder')}
          </Button>
          <p className="display-files-by-lead__popup__reminder">
            {t('reminderSendNotificationToTheLead')}
          </p>
        </>
      )}
    </div>
  );

  const trigger = (
    <DisplayFilesByCategoryTrigger
      className={classes}
      count={count}
      state={state}
      {...(!isOpen && { popupContent: t(POPUP_CONTENT[state]) })}
      onClick={() => {
        if (!canOpen) return;

        onToggleOpen();
      }}
    />
  );

  if (!isAboveLaptop)
    return (
      <>
        {trigger}
        <Modal
          className="display-files-by-lead__modal"
          open={isOpen}
          controlledByParent
          onClose={onClose}
          onX={onClose}
        >
          {content}
        </Modal>
      </>
    );

  return (
    <Dropdown
      renderAsPortal
      portalClassName="display-files-by-lead__popup-portal"
      disabled={!canOpen}
      contentClassName="display-files-by-lead__popup"
      onClick={(e) => e.stopPropagation()}
      onOpen={() => !isOpen && onOpen()}
      onClose={() => isOpen && onClose()}
      trigger={trigger}
    >
      {content}
    </Dropdown>
  );
};

export default DisplayFilesByLeadCategory;
